import Link from 'next/link'
import Image from 'next/image'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Course } from '../[slug]/courseData'
import { toIsoDuration } from '@/lib/isoDuration'

export default function CourseCard({
  course,
  priority = false,
}: {
  course: Course
  priority?: boolean
}) {
  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.title,
    description: course.description,
    provider: {
      '@type': 'Organization',
      name: 'AI Builder Club',
    },
    courseMode: 'online',
    educationalLevel: course.level ?? 'intermediate',
    ...(course.image
      ? { image: `https://www.aibuilderclub.com${course.image}` }
      : {}),
    ...(toIsoDuration(course.duration) ? { timeRequired: toIsoDuration(course.duration) } : {}),
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(courseSchema) }}
      />
      <Link
        href={`/courses/${course.id}`}
        className="block h-full"
        aria-label={`Learn more about ${course.title}`}
      >
        <article className="h-full bg-[#111111] border border-gray-800 rounded-2xl md:rounded-3xl overflow-hidden transition-all duration-300 hover:bg-[#141414] hover:border-gray-700 md:hover:scale-105 shadow-lg cursor-pointer">
          <div className="flex flex-row md:flex-col h-full">
            <div className="w-28 h-28 flex-shrink-0 md:w-full md:h-auto md:aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative rounded-l-2xl md:rounded-l-none md:rounded-t-2xl overflow-hidden">
              {course.image ? (
                <Image
                  src={course.image}
                  alt={`${course.title} - ${course.description.substring(0, 50)}...`}
                  width={400}
                  height={225}
                  className="w-full h-full object-cover"
                  priority={priority}
                  loading={priority ? 'eager' : 'lazy'}
                />
              ) : (
                <div className="text-3xl md:text-6xl">{course.icon}</div>
              )}
            </div>

            <div className="p-3 md:p-6 flex-1 flex flex-col justify-center md:justify-start">
              <header className="mb-1 md:mb-4">
                <h3 className="font-semibold text-sm md:text-lg text-white text-left line-clamp-2">
                  {course.subtitle}
                </h3>
              </header>

              <div className="hidden md:block mb-6">
                <p className="text-gray-400 text-sm text-left">
                  {course.description}
                </p>
              </div>

              <p className="text-gray-400 text-xs text-left line-clamp-2 md:hidden mb-2">
                {course.description}
              </p>

              <div className="hidden md:flex justify-start md:mt-auto">
                <Button className="bg-white text-black hover:bg-white/90 py-2 px-4 rounded-full font-medium transition-colors text-sm inline-flex items-center gap-2">
                  View Course
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </article>
      </Link>
    </>
  )
}
